(ns io.daskul.webapp.widgets.form.field
  "Aggregate-aware form field renderer.

   Renders appropriate input widget based on schema and UI hints.
   Subscribes to field-level errors and clears them on edit.
   Delegates selector rendering to lookup-field (which uses selector-field)."
  (:require
   [clojure.string :as str]
   [uix.core :refer [defui $]]
   [uix.re-frame :as urf]
   [re-frame.core :as rf]
   ["dayjs" :as dayjs]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.format :as format]
   [io.daskul.webapp.aggregates.events :as agg.events]
   [io.daskul.webapp.aggregates.subs :as agg.subs]
   [io.daskul.webapp.widgets.display :as display]
   [io.daskul.webapp.widgets.form.errors :as form.errors]
   [io.daskul.webapp.widgets.form.input-type :as input-type]
   [io.daskul.webapp.widgets.form.lookup :refer [lookup-field]]
   [io.daskul.webapp.widgets.form.value-with-unit :refer [value-with-unit-input]]
   [io.daskul.webapp.widgets.form.inline-table :refer [inline-table-widget]]
   [io.daskul.webapp.widgets.form.collection :refer [collection-widget]]
   [io.daskul.webapp.widgets.form.weekday-picker :refer [weekday-picker-field]]))

;; ============================================================================
;; Helpers — re-exported from the pure helper namespaces for existing callers
;; ============================================================================

(def humanize-error form.errors/humanize-error)
(def form-level-error-message form.errors/form-level-error-message)

(def ^:private unwrap-maybe input-type/unwrap-maybe)
(def schema->input-type input-type/schema->input-type)

(defn enum-options
  "Extract options from [\"enum\" ...] or [\"maybe\" [\"enum\" ...]] schema."
  [schema]
  (let [effective (unwrap-maybe schema)]
    (when (and (vector? effective) (= "enum" (first effective)))
      (rest effective))))

(defn multi-select-options
  "Extract options for multi-select from schema or ui hints."
  [schema ui]
  (let [options-map (:options ui)
        make-option (fn [v]
                      {:value v
                       :label (or (get options-map v)
                                  (display/humanize-enum-value v)
                                  v)})]
    (or
     (when (and (vector? schema) (= "vector" (first schema)))
       (let [inner-schema (second schema)]
         (when (and (vector? inner-schema) (= "enum" (first inner-schema)))
           (mapv make-option (rest inner-schema)))))
     (when-let [opts (enum-options schema)]
       (mapv make-option opts)))))

;; ============================================================================
;; Field Widget Renderer (pure function — no hooks or subscriptions)
;; ============================================================================

(declare form-field)

(defn- auto-capitalize
  "Convert keyword name to title case: :first-name -> \"First Name\"."
  [field-key]
  (->> (str/split (name field-key) #"-")
       (map str/capitalize)
       (str/join " ")))

(defn- render-field-widget
  "Pure render function for a single field widget. No hooks or subscriptions.
   Called by form-field and recursively by compound sub-fields."
  [{:keys [input-type label value handle-change disabled required? field-error
           field ui schema agg-key form-data on-auto-fill set-form-field!]}]
  (case input-type
    :selector
    ($ lookup-field {:agg-key agg-key :field field :value value
                     :on-change handle-change :disabled disabled
                     :form-data form-data
                     :required required?
                     :error field-error
                     :form-field-component form-field
                     :on-auto-fill on-auto-fill})

    :inline-table
    ($ inline-table-widget
       {:field-key (:name field)
        :value value
        :set-field! handle-change
        :form-data (or form-data {})
        :ui ui
        :relationship nil})

    :collection
    ($ collection-widget
       {:field-key (:name field)
        :value value
        :set-field! handle-change
        :schema schema
        :ui ui})

    :compound
    (let [sub-fields (:fields ui)
          map-value (or value {})
          update-sub (fn [k v]
                       (handle-change (assoc map-value k v)))]
      ($ ui/paper {:withBorder true :p "sm" :radius "md"}
         ($ ui/text {:size "sm" :fw 600 :mb "xs"} label)
         ($ :div {:style #js {:display "grid"
                               :gridTemplateColumns "repeat(12, 1fr)"
                               :gap "8px"}}
            (for [{sub-key :key sub-label :label span :span
                   sub-schema :schema sub-ui :ui
                   :or {span 6}} sub-fields]
              (let [sub-value (get map-value sub-key)
                    effective-schema (or sub-schema "string")
                    effective-ui (merge {:label sub-label} sub-ui)
                    sub-input-type (schema->input-type effective-schema effective-ui)
                    sub-field-map {:name sub-key :schema effective-schema :ui effective-ui}]
                ($ :div {:key (name sub-key)
                         :style #js {:gridColumn (str "span " span)}}
                   (render-field-widget
                     {:input-type sub-input-type
                      :label sub-label
                      :value sub-value
                      :handle-change #(update-sub sub-key %)
                      :disabled disabled
                      :required? false
                      :field-error nil
                      :field sub-field-map
                      :ui effective-ui
                      :schema effective-schema
                      :agg-key agg-key
                      :form-data map-value
                      :on-auto-fill on-auto-fill})))))))

    :date
    (let [fmt (or (:format ui) "YYYY-MM-DD")
          ;; a :min-date / :max-date bound → js/Date: :today, or an ISO date string.
          ;; :today arrives as the string "today" over JSON, so accept both.
          resolve-bound (fn [b] (cond (nil? b) nil
                                      (or (= :today b) (= "today" b)) (js/Date.)
                                      :else (.toDate (dayjs b))))
          date-value (when (and value (seq (str value)))
                       (.toDate (dayjs value)))
          on-date-change (fn [date-obj]
                           (if date-obj
                             (handle-change (.format (dayjs date-obj) "YYYY-MM-DD"))
                             (handle-change nil)))]
      ($ ui/date-input
         {:label label
          :description (:description ui)
          :value date-value
          :onChange on-date-change
          :valueFormat fmt
          :placeholder (or (:placeholder ui) fmt)
          ;; opt-in bounds (industry min/max — like HTML / Mantine): :today or an ISO date
          :maxDate (resolve-bound (:max-date ui))
          :minDate (resolve-bound (:min-date ui))
          :highlightToday true
          :allowDeselect false
          :clearable true
          :required required?
          :disabled disabled
          :error field-error}))

    :time
    ($ ui/time-input
       {:label label
        :description (:description ui)
        :value (or value "")
        :onChange #(handle-change (.. % -target -value))
        :required required?
        :disabled disabled
        :error field-error})

    :weekday-toggle
    ($ weekday-picker-field
       {:label label
        :value value
        :on-change handle-change
        :required? required?
        :disabled? disabled
        :error field-error
        :description (:description ui)})

    :switch
    ($ ui/switch
       {:label label
        :description (:description ui)
        :checked (boolean value)
        :onChange #(handle-change (.. % -target -checked))
        :disabled disabled
        :error field-error})

    :value-with-unit
    (let [{:keys [unit-field units]} (:options ui)
          unit-key (keyword unit-field)]
      ($ value-with-unit-input
         {:label label
          :description (:description ui)
          :required? required?
          :disabled disabled
          :error field-error
          :value value
          :on-change handle-change
          :units units
          :unit (get form-data unit-key)
          :on-unit-change #(when set-form-field! (set-form-field! unit-key %))}))

    :number
    (let [money? (= :currency (some-> (get-in ui [:display :type]) keyword))]
      ($ ui/number-input
         (merge {:label label
                 :description (:description ui)
                 :value (if (nil? value) "" value)
                 :placeholder (:placeholder ui)
                 :onChange #(handle-change (format/parse-number %))
                 :hideControls true
                 :required required?
                 :disabled disabled
                 :error field-error}
                (if money?
                  (format/currency-input-props)
                  (format/number-input-props)))))

    :textarea
    ($ ui/textarea
       {:label label
        :value (or value "")
        :placeholder (:placeholder ui)
        :onChange #(handle-change (.. % -target -value))
        :minRows 3
        :autosize true
        :required required?
        :disabled disabled
        :error field-error})

    :email
    ($ ui/text-input
       {:label label
        :type "email"
        :value (or value "")
        :onChange #(handle-change (.. % -target -value))
        :required required?
        :disabled disabled
        :error field-error})

    :password
    ($ ui/password-input
       {:label label
        :value (or value "")
        :onChange #(handle-change (.. % -target -value))
        :required required?
        :disabled disabled
        :error field-error})

    ;; default: text input
    ($ ui/text-input
       {:label label
        :description (:description ui)
        :value (or value "")
        :onChange #(handle-change (.. % -target -value))
        :required required?
        :disabled disabled
        :error field-error})))

;; ============================================================================
;; Form Field (aggregate-aware thin shell — stateful, delegates to render-field-widget)
;; ============================================================================

(defui form-field
  "Aggregate-aware form field component.

   Thin shell that handles stateful concerns (error subscriptions, sentry masking)
   and delegates rendering to render-field-widget.

   Props:
   - agg-key: keyword - aggregate key for re-frame state
   - field: map - field definition with :name, :schema, :ui, :required
   - value: any - current field value
   - on-change: fn - called with new value
   - disabled: boolean
   - form-data: map - full form state
   - external-error: string - optional, overrides the default error subscription
   - on-clear-error: fn - optional, called instead of default clear-field-error dispatch
   - on-auto-fill: fn | nil - forwarded to lookup-field for auto-fill support"
  [{:keys [agg-key field value on-change disabled form-data
           external-error on-clear-error on-auto-fill set-form-field!]}]
  (let [field-key (:name field)
        schema (:schema field)
        ui (:ui field)
        security (:security field)
        label (or (:label ui) (auto-capitalize field-key))
        input-type (schema->input-type schema ui)
        required? (:required field)
        ;; Sentry masking: mask if :mask true, or :pii true (unless :mask explicitly false)
        sentry-mask? (when security
                       (or (:mask security)
                           (and (:pii security) (not (false? (:mask security))))))
        ;; Use external error if provided (e.g., child forms), otherwise subscribe to standard path
        default-error (humanize-error (urf/use-subscribe [::agg.subs/field-error agg-key field-key]))
        field-error (if (some? external-error) external-error default-error)
        ;; Clear field error when user starts editing
        handle-change (fn [v]
                        (when field-error
                          (if on-clear-error
                            (on-clear-error)
                            (rf/dispatch [::agg.events/clear-field-error agg-key field-key])))
                        (on-change v))
        wrap-mask (fn [content]
                    (if sentry-mask?
                      ($ :div {:data-sentry-mask true} content)
                      content))]

    (wrap-mask
      (render-field-widget
        {:input-type input-type :label label :value value
         :handle-change handle-change :disabled disabled
         :set-form-field! set-form-field!
         :required? required? :field-error field-error
         :field field :ui ui :schema schema
         :agg-key agg-key :form-data form-data
         :on-auto-fill on-auto-fill}))))
